import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Spinner,
  Input,
  Collapse,
} from "reactstrap";
import Select from "react-select";
import api from "../helpers/api";
import PermissionGuard from "../components/Common/PermissionGuard";
import { ROLE_MANAGE } from "../constants/permissions";
import CreateRoleModal from "../components/CreateRoleModal";
import PermissionsTable from "../components/PermissionsTable";

const Roles = () => {
  useEffect(() => {
    document.title = "Roles & Permissions | ArcMedix Admin";
  }, []);

  const [roles, setRoles] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Fetch organizations for filter dropdown
  const fetchOrganizations = useCallback(async () => {
    try {
      const { data } = await api.get("organization/all");
      const orgOptions = (data.response || []).map((org) => ({
        value: org.id,
        label: org.name,
      }));
      setOrganizations(orgOptions);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  }, []);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = selectedOrg ? `?organizationId=${selectedOrg.value}` : "";
      const { data } = await api.get(`rbac/roles${params}`);
      setRoles(data.response || []);
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError("Failed to load roles. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  // Fetch grouped permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("rbac/permissions/grouped");
      setGroupedPermissions(data.response || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
    fetchPermissions();
  }, [fetchOrganizations, fetchPermissions]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Filter roles by search term
  const filteredRoles = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return roles.filter(
      (role) =>
        role.name?.toLowerCase().includes(searchLower) ||
        role.displayName?.toLowerCase().includes(searchLower) ||
        role.description?.toLowerCase().includes(searchLower)
    );
  }, [roles, searchTerm]);

  // Toggle role expansion
  const toggleExpand = useCallback((roleId) => {
    setExpandedRole((prev) => (prev === roleId ? null : roleId));
  }, []);

  // Get role permissions as a Set for quick lookup
  const getRolePermissionSet = (role) => {
    if (!role.permissions) return new Set();
    return new Set(role.permissions.map((p) => p.name || p));
  };

  // Handle successful role creation
  const handleRoleCreated = () => {
    setCreateModalOpen(false);
    fetchRoles();
  };

  return (
    <div className="page-content mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3 w-100"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission={ROLE_MANAGE}>
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={() => setCreateModalOpen(true)}
            >
              <FeatherIcon icon="plus" size={18} />
              Create Role
            </button>
          </PermissionGuard>

          {/* Organization Filter */}
          <div style={{ minWidth: "250px" }}>
            <Select
              options={organizations}
              value={selectedOrg}
              onChange={setSelectedOrg}
              isClearable
              placeholder="Filter by Organization..."
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          <div
            className="ms-auto"
            style={{ minWidth: "250px", flexGrow: 1, maxWidth: "350px" }}
          >
            <Input
              type="text"
              placeholder="Search Roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader>
          <h3 className="card-title text-success mb-0">Roles & Permissions</h3>
        </CardHeader>
        <CardBody>
          {error && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center mb-3">
              <span>{error}</span>
              <button className="btn btn-sm btn-outline-danger" onClick={fetchRoles}>
                <FeatherIcon icon="refresh-cw" size={14} className="me-1" />
                Retry
              </button>
            </div>
          )}
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading roles...</p>
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="text-center py-5">
              <FeatherIcon icon="shield-off" size={48} className="text-muted mb-3" />
              <p className="text-muted">No roles found</p>
            </div>
          ) : (
            <AnimatePresence>
              {filteredRoles.map((role, index) => {
                const permissionSet = getRolePermissionSet(role);
                const isExpanded = expandedRole === role.id;

                return (
                  <motion.div
                    key={role.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Card className="mb-3 shadow-sm border">
                      <CardHeader
                        className="d-flex justify-content-between align-items-center py-3"
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleExpand(role.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleExpand(role.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 40,
                              height: 40,
                              backgroundColor: "rgba(var(--bs-primary-rgb), 0.1)",
                            }}
                          >
                            <FeatherIcon
                              icon="shield"
                              size={20}
                              className="text-primary"
                            />
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold">
                              {role.displayName || role.name}
                            </h6>
                            <small className="text-muted">
                              {role.description || "No description"}
                            </small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <span className="text-muted">
                            {role.userCount || 0} {role.userCount === 1 ? "user" : "users"}
                          </span>
                          <FeatherIcon
                            icon={isExpanded ? "chevron-up" : "chevron-down"}
                            size={18}
                            className="text-muted"
                          />
                        </div>
                      </CardHeader>

                      <Collapse isOpen={isExpanded}>
                        <CardBody className="border-top">
                          {groupedPermissions.length === 0 ? (
                            <div className="text-center py-3">
                              <Spinner size="sm" color="primary" />
                              <span className="ms-2">Loading permissions...</span>
                            </div>
                          ) : (
                            <PermissionsTable
                              groupedPermissions={groupedPermissions}
                              rolePermissions={permissionSet}
                              roleName={role.name}
                            />
                          )}
                        </CardBody>
                      </Collapse>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </CardBody>
      </Card>

      {/* Create Role Modal */}
      <CreateRoleModal
        isOpen={createModalOpen}
        toggle={() => setCreateModalOpen(false)}
        onSuccess={handleRoleCreated}
        groupedPermissions={groupedPermissions}
      />
    </div>
  );
};

export default Roles;
